import type { SortingState } from '@tanstack/react-table';

export type QueryValue = string | number | undefined | null | Array<string | number>;

export interface ListQueryInput {
  page?: number;
  per_page?: number;
  q?: Record<string, QueryValue>;
  sort?: SortingState;
}

/**
 * Builds a URLSearchParams object for the standard ransack/pagy list endpoint
 * shape used by the accounting services. Returns URLSearchParams (not a plain
 * object) so multi-valued keys (`q[s][]=...` for sort, `q[year_in][]=...` for
 * `_in` predicates) round-trip cleanly through axios without needing a custom
 * paramsSerializer.
 *
 * Array values become repeated `q[key][]=v1&q[key][]=v2` params, which ransack
 * expects for `_in` predicates (and similar array predicates).
 */
export const buildListQuery = ({ page, per_page, q, sort }: ListQueryInput = {}): URLSearchParams => {
  const params = new URLSearchParams();
  if (page) params.set('page', String(page));
  if (per_page) params.set('per_page', String(per_page));
  if (q) {
    for (const [key, value] of Object.entries(q)) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) {
        // Skip empty arrays so callers can pass [] without polluting the query
        if (value.length === 0) continue;
        for (const v of value) {
          if (v !== undefined && v !== null && v !== '') {
            params.append(`q[${key}][]`, String(v));
          }
        }
      } else {
        params.set(`q[${key}]`, String(value));
      }
    }
  }
  if (sort && sort.length > 0) {
    for (const { id, desc } of sort) {
      params.append('q[s][]', `${id} ${desc ? 'desc' : 'asc'}`);
    }
  }
  return params;
};
